/**
 * 連擊系統 - 模組化實作
 *
 * 透過 ComboRegistry 來管理可擴充的連擊等級，
 * 允許自由定義從 C ~ SSS 的各種連擊與對應特效。
 */

import type { PlayerColor } from '@/types/game-types';
import { t } from '@/i18n/index';
import {
  ComboRegistry,
  type ComboEffect,
  type ComboTierConfig,
  type ComboTierId,
  createDefaultComboRegistry,
} from './combo-registry';
import {
  blendPresets,
  getPresetForSpecialMove,
  type ParticlePresetConfig,
  type ParticlePresetType,
} from '@/effects/particle-presets';

export interface ComboState {
  count: number;
  player: PlayerColor | null;
  multiplier: number;
  startTime: number;
  isActive: boolean;
  maxCombo: number;
  type: ComboTierId;
}

export interface ComboStatistics {
  currentCombo: number;
  maxCombo: number;
  totalCombos: number;
  averageComboLength: number;
  comboTypeDistribution: Record<string, number>;
}

export type ComboEventType = 'combo_started' | 'combo_continued' | 'combo_ended' | 'combo_broken';

export interface ComboEvent {
  type: ComboEventType;
  player: PlayerColor;
  comboCount: number;
  comboType: ComboTierId;
  tier: ComboTierConfig;
  effect?: ComboEffect;
  timestamp: number;
}

/**
 * 自訂連擊特效倍率函式。
 * 允許擴展套件在原本的強度計算之外，進一步調整時長或震撼效果。
 */
export type ComboEffectScaler = (comboCount: number, effect: ComboEffect) => ComboEffect;

/**
 * 擴展套件資料結構：
 * - tiers: 新增或覆蓋的連擊等級設定
 * - effectScaler: 自訂強度調整邏輯
 * - onEvent: 監聽連擊事件以觸發額外行為
 * - replaceExistingTiers: 是否覆蓋既有 tiers（預設 true）
 */
export interface ComboExtensionPack {
  tiers?: ComboTierConfig[];
  effectScaler?: ComboEffectScaler;
  onEvent?: (event: ComboEvent, system: ComboSystem) => void;
  replaceExistingTiers?: boolean;
}

export class ComboSystem {
  private readonly _registry: ComboRegistry;
  private _state: ComboState;
  private _eventHandlers: ((event: ComboEvent) => void)[] = [];
  private _history: ComboEvent[] = [];
  private readonly _customEffectScalers: ComboEffectScaler[] = [];
  private readonly _extensionHandlers = new Set<(event: ComboEvent) => void>();

  constructor(registry: ComboRegistry = createDefaultComboRegistry()) {
    this._registry = registry;
    this._state = this.createInitialState();
  }

  get state(): Readonly<ComboState> {
    return Object.freeze({ ...this._state });
  }

  get history(): readonly ComboEvent[] {
    return Object.freeze([...this._history]);
  }

  get registry(): ComboRegistry {
    return this._registry;
  }

  /**
   * 套用外部擴展設定，支援一次性注入多個連擊等級與效果調整。
   */
  applyExtensionPack(pack: ComboExtensionPack): void {
    if (pack.tiers?.length) {
      this._registry.registerTierBatch(pack.tiers, { replaceExisting: pack.replaceExistingTiers });
      const currentTier = this._registry.getTierForCount(this._state.count);
      this._state = { ...this._state, type: currentTier.id };
    }

    if (pack.effectScaler) {
      this._customEffectScalers.push(pack.effectScaler);
    }

    if (pack.onEvent) {
      const handler = (event: ComboEvent): void => {
        pack.onEvent!(event, this);
      };
      this.addEventListener(handler);
      this._extensionHandlers.add(handler);
    }
  }

  processMove(player: PlayerColor, opponentCanMove: boolean): ComboEvent | null {
    if (opponentCanMove) {
      return this.endCombo();
    }

    if (this._state.player === player) {
      return this.continueCombo();
    }

    return this.startCombo(player);
  }

  breakCombo(): ComboEvent | null {
    if (!this._state.isActive || !this._state.player) {
      return null;
    }

    const tier = this._registry.getTierById(this._state.type);
    if (!tier) {
      return null;
    }

    const event: ComboEvent = {
      type: 'combo_broken',
      player: this._state.player,
      comboCount: this._state.count,
      comboType: this._state.type,
      tier,
      timestamp: Date.now(),
    };

    const previousMax = this._state.maxCombo;
    this._state = this.createInitialState(previousMax);
    this._emitEvent(event);
    this._history.push(event);

    return event;
  }

  getComboEffect(): ComboEffect | undefined {
    const tier = this._registry.getTierById(this._state.type);
    if (!tier) {
      return undefined;
    }

    return this.scaleEffectIntensity(tier.effect, this._state.count);
  }

  getSuggestedEffect(comboType: ComboTierId): ParticlePresetType {
    const tier = this._registry.getTierById(comboType);
    return tier?.effect.effectType ?? 'snowflake';
  }

  shouldPlaySpecialSound(comboType: ComboTierId): boolean {
    const tier = this._registry.getTierById(comboType);
    return Boolean(tier?.effect.soundEffect);
  }

  shouldTriggerScreenShake(comboType: ComboTierId): boolean {
    const tier = this._registry.getTierById(comboType);
    return Boolean(tier?.effect.screenShake);
  }

  shouldTriggerSlowMotion(comboType: ComboTierId): boolean {
    const tier = this._registry.getTierById(comboType);
    return Boolean(tier?.effect.slowMotion);
  }

  getComboDisplayText(): string {
    if (!this._state.isActive) {
      return '';
    }

    const tier = this._registry.getTierById(this._state.type);
    const key = tier?.labelKey ?? `combo.${this._state.type}`;
    const text = t(key);
    return text === key ? `${this._state.count} combo!` : text;
  }

  getComboDisplayColor(comboType?: ComboTierId): string {
    const tierId = comboType ?? this._state.type;
    const tier = this._registry.getTierById(tierId);
    return tier?.displayColor ?? '#ffffff';
  }

  getStats(): ComboStatistics {
    const combos = this._history.filter(event => event.type === 'combo_ended' || event.type === 'combo_broken');
    const totalCombos = combos.length;
    const averageLength = totalCombos > 0
      ? combos.reduce((sum, combo) => sum + combo.comboCount, 0) / totalCombos
      : 0;

    const distribution: Record<string, number> = {};
    this._registry.getAllTiers().forEach(tier => {
      distribution[tier.id] = 0;
    });

    combos.forEach(combo => {
      distribution[combo.comboType] = (distribution[combo.comboType] ?? 0) + 1;
    });

    return {
      currentCombo: this._state.count,
      maxCombo: this._state.maxCombo,
      totalCombos,
      averageComboLength: averageLength,
      comboTypeDistribution: distribution,
    };
  }

  addEventListener(handler: (event: ComboEvent) => void): void {
    this._eventHandlers.push(handler);
  }

  removeEventListener(handler: (event: ComboEvent) => void): void {
    const index = this._eventHandlers.indexOf(handler);
    if (index !== -1) {
      this._eventHandlers.splice(index, 1);
    }
  }

  reset(): void {
    this._state = this.createInitialState();
    this._history = [];
  }

  private startCombo(player: PlayerColor): ComboEvent {
    if (this._state.isActive) {
      this.endCombo();
    }

    const tier = this._registry.getTierForCount(2);

    this._state = {
      count: 2,
      player,
      multiplier: 1.2,
      startTime: Date.now(),
      isActive: true,
      maxCombo: Math.max(this._state.maxCombo, 2),
      type: tier.id,
    };

    const event = this.createComboEvent('combo_started', tier);
    this._emitEvent(event);
    this._history.push(event);
    return event;
  }

  private continueCombo(): ComboEvent {
    if (!this._state.isActive || !this._state.player) {
      throw new Error('Cannot continue combo: no active combo');
    }

    this._state.count++;
    this._state.multiplier = Math.min(1.0 + this._state.count * 0.2, 3.0);
    this._state.maxCombo = Math.max(this._state.maxCombo, this._state.count);

    const tier = this._registry.getTierForCount(this._state.count);
    this._state.type = tier.id;

    const event = this.createComboEvent('combo_continued', tier);
    this._emitEvent(event);
    this._history.push(event);
    return event;
  }

  private endCombo(): ComboEvent | null {
    if (!this._state.isActive || !this._state.player) {
      return null;
    }

    const tier = this._registry.getTierById(this._state.type);
    if (!tier) {
      return null;
    }

    const event = this.createComboEvent('combo_ended', tier);
    const previousMax = this._state.maxCombo;
    this._state = this.createInitialState(previousMax);
    this._emitEvent(event);
    this._history.push(event);
    return event;
  }

  private createInitialState(maxCombo = 0): ComboState {
    const baseTier = this._registry.getTierForCount(0);
    return {
      count: 0,
      player: null,
      multiplier: 1.0,
      startTime: 0,
      isActive: false,
      maxCombo,
      type: baseTier.id,
    };
  }

  private createComboEvent(type: ComboEventType, tier: ComboTierConfig): ComboEvent {
    const effect = this.scaleEffectIntensity(tier.effect, this._state.count);
    return {
      type,
      player: this._state.player!,
      comboCount: this._state.count,
      comboType: tier.id,
      tier,
      effect,
      timestamp: Date.now(),
    };
  }

  private scaleEffectIntensity(effect: ComboEffect, comboCount: number): ComboEffect {
    const base = this.applyDefaultEffectScaling(effect, comboCount);
    return this._customEffectScalers.reduce(
      (current, scaler) => scaler(comboCount, current),
      base,
    );
  }

  /**
   * 預設的強度演算法：連擊越高，強度與持續時間越長。
   */
  private applyDefaultEffectScaling(effect: ComboEffect, comboCount: number): ComboEffect {
    const intensityBonus = Math.min(comboCount * 0.1, 1.0);
    return {
      ...effect,
      intensity: effect.intensity + intensityBonus,
      duration: effect.duration + comboCount * 50,
    };
  }

  private _emitEvent(event: ComboEvent): void {
    this._eventHandlers.forEach(handler => {
      try {
        handler(event);
      } catch (error) {
        console.error('Combo event handler error:', error);
      }
    });
  }

  blendComboPreset(comboType: ComboTierId, withPreset: ParticlePresetType): ParticlePresetConfig {
    const tier = this._registry.getTierById(comboType);
    if (!tier) {
      return blendPresets(withPreset, withPreset);
    }

    const basePreset = tier.effect.effectType;
    return blendPresets(basePreset, withPreset);
  }

  getRecommendedSpecialMovePreset(
    moveType: 'corner' | 'massive' | 'comeback' | 'domination' | 'combo',
  ): ParticlePresetType {
    return getPresetForSpecialMove(moveType);
  }
}
